import {
	SBButton,
	SBBox,
	SBTypography,
	SBTextField,
} from "modules/Commons";
import { useState } from "react";
import logo from "assets/logo.svg";
import LoginFormInvalid from "./LoginFormInvalid";
import useLoadingScope from "hooks/useLoadingScope";
import { SBTranslate } from "i18n";
import useRememberMe from "hooks/useRememberMe";
import { useAccountRequester } from "modules/Accounts/hooks/useAccountRequester";
import { isPasswordLoginDomain } from "utils/auth/passwordLoginDomain";

export default function LoginForm({
	onLogin,
	isAuthenticating,
}: LoginFormProps) {
	const { getRememberMe, setRememberMe } = useRememberMe();
	const { loading, useLoading } = useLoadingScope();
	const accountRequester = useAccountRequester();
	const passwordLogin = isPasswordLoginDomain();

	const [invalidLogin, setInvalidLogin] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [rememberMeCheckbox, setRememberMeCheckbox] = useState<boolean>(
		getRememberMe()
	);

	const onSubmit = async () => {
		await useLoading(async () => {
			setInvalidLogin(false);

			const trimmedEmail = email.trim();

			if (!trimmedEmail) return;

			if (passwordLogin) {
				try {
					await onLogin(trimmedEmail, password);
				} catch (error) {
					setInvalidLogin(true);
				}
				return;
			}

			const exists = await accountRequester.validateAccount(trimmedEmail);

			if (!exists) {
				setInvalidLogin(true);
				return;
			}

			await onLogin(trimmedEmail);
		});
	};

	const handleRememberMe = (value: boolean) => {
		setRememberMeCheckbox(value);
		setRememberMe(value);
	};

	return (
		<SBBox display="flex" flexDirection="column" alignItems="start">
			<img src={logo} height="64" alt="logo" />

			<SBTypography mt={15} variant="h4" fontWeight="700" color="gray2">
				{SBTranslate("welcome")}
			</SBTypography>

			<SBTypography variant="body1" fontWeight="400" my={3}>
				{SBTranslate(
					passwordLogin ? "insertAccessDataPassword" : "insertAccessData"
				)}
			</SBTypography>

			{invalidLogin && (
				<LoginFormInvalid onClose={(value) => setInvalidLogin(value)} />
			)}

			<SBTextField
				placeholder={SBTranslate("emailPlaceholder")}
				onChange={(e) => {
					setEmail(e.target.value);
					if (invalidLogin) setInvalidLogin(false);
				}}
				value={email}
				title={SBTranslate("email")}
				data-testid="login-form-email"
			/>

			{passwordLogin && (
				<SBTextField
					type="password"
					placeholder={SBTranslate("yourPassword")}
					title={SBTranslate("password")}
					onChange={(e) => {
						setPassword(e.target.value);
						if (invalidLogin) setInvalidLogin(false);
					}}
					value={password}
					data-testid="login-form-password"
				/>
			)}

			<SBBox mx="auto" width={1} mt={1}>
				<SBButton
					variant="contained"
					fullWidth
					style={{ textTransform: "capitalize" }}
					onClick={onSubmit}
					loading={loading || isAuthenticating}
					disabled={loading || isAuthenticating}
					data-testid="login-form-submit"
				>
					{SBTranslate("login")}
				</SBButton>
			</SBBox>
		</SBBox>
	);
}

interface LoginFormProps {
	onLogin: (email: string, password?: string) => Promise<void>;
	isAuthenticating: boolean;
}